/**
 * Game - Main game controller
 * Manages the game loop, state machine, and coordinates all subsystems.
 *
 * Game States:
 *   - 'loading'    : Assets loading
 *   - 'title'      : Title/menu screen
 *   - 'playing'    : Active gameplay
 *   - 'paused'     : Gameplay paused
 *   - 'waveIntro'  : Wave intro screen
 *   - 'waveEnd'    : End-of-wave bonus tally
 *   - 'gameOver'   : Game over screen
 *   - 'highScores' : High score table
 *   - 'settings'   : Settings screen
 */

import { InputManager } from './InputManager.js';
import { AudioManager } from '../audio/AudioManager.js';
import { StateManager } from './StateManager.js';
import { Renderer } from './Renderer.js';

export class Game {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.lastTime = 0;
    this.running = false;
    this.assetsLoaded = false;

    // Subsystems (to be initialized)
    this.input = null;
    this.audio = null;
    this.state = null;
    this.renderer = null;
  }

  /**
   * Initialize all game subsystems and start the game loop.
   */
  init() {
    // Set up canvas size
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    // Initialize subsystems
    this.input = new InputManager(this.canvas);
    this.audio = new AudioManager();
    this.state = new StateManager(this);
    this.renderer = new Renderer(this.ctx, this.canvas);

    // Get the UI overlay element for DOM-based text display (used for Playwright testing)
    this.uiOverlay = document.getElementById('ui-overlay');

    // Handle visibility change (auto-pause when tab loses focus)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.state.currentState === 'playing') {
        this.state.setState('paused', { playingState: this.state.states.playing });
      }
    });

    // Also handle window blur (e.g., alt-tab to another application)
    window.addEventListener('blur', () => {
      if (this.state && this.state.currentState === 'playing') {
        this.state.setState('paused', { playingState: this.state.states.playing });
      }
    });

    // Start the game loop
    this.running = true;
    this.lastTime = performance.now();
    requestAnimationFrame((time) => this.gameLoop(time));

    console.log('Missile Command initialized successfully.');
  }

  /**
   * Main game loop using requestAnimationFrame with delta-time.
   */
  gameLoop(currentTime) {
    if (!this.running) return;

    const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
    this.lastTime = currentTime;

    // Clamp delta to prevent huge jumps (e.g., after tab switch)
    const clampedDelta = Math.min(deltaTime, 0.05);

    this.update(clampedDelta);
    this.render();

    requestAnimationFrame((time) => this.gameLoop(time));
  }

  /**
   * Update game logic based on current state.
   */
  update(dt) {
    this.state.update(dt);
    this.input.update();
  }

  /**
   * Render the current frame.
   */
  render() {
    // Clear the canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Delegate rendering to the current state
    this.state.render(this.ctx);

    // Update DOM overlay with current state for accessibility/testing
    this._updateOverlay();
  }

  /**
   * Update the DOM overlay with current game state info.
   * This provides a testable DOM element for Playwright text detection.
   */
  _updateOverlay() {
    if (!this.uiOverlay) return;

    const state = this.state.currentState;
    this.uiOverlay.setAttribute('data-game-state', state);
    this.uiOverlay.setAttribute('data-assets-loaded', this.assetsLoaded ? 'true' : 'false');
  }

  /**
   * Resize canvas to fill the viewport while maintaining quality.
   * Notifies the current state so game entities can reposition proportionally.
   */
  resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Track old dimensions for proportional repositioning
    const oldWidth = this.width || width;
    const oldHeight = this.height || height;

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';
    this.ctx.scale(dpr, dpr);

    // Store logical dimensions for game calculations
    this.width = width;
    this.height = height;

    // Notify state manager so active states can reposition entities
    if (this.state && (oldWidth !== width || oldHeight !== height)) {
      this.state.onResize(oldWidth, oldHeight, width, height);
    }
  }
}
